import { IconsEnum } from '../../../../../../../assets/icons/types';
import {
  OnLifestyleSimilarSearchAction,
  UpdateLifestyleCacheAction
} from '../../../../../../lifestyles/lifestylesTypes';
import { ThreeDStockLifestylesListItemButtonsLifestyle } from './ThreeDStockLifestylesListItemButtons.types';
import { TeamNanoID } from '../../../../../../teams/teamsTypes';

import { DownloadLifestyleButton } from '../../../../../../lifestyles/components/buttons/DownloadLifestyleButton';
import { DownloadLifestyleSeparatelyButton } from '../../../../../../lifestyles/components/buttons/DownloadLifestyleSeparatelyButton';
import { LifestyleAttachModalButton } from '../../../../../../lifestyles/components/buttons/LifestyleAttachModalButton';
import { LifestyleCopyLinkButton } from '../../../../../../lifestyles/components/buttons/LifestyleCopyLinkButton';
import { LifestyleFavoriteButton } from '../../../../../../lifestyles/components/buttons/LifestyleFavoriteButton';
import { LifestyleSearchProductsButton } from '../../../../../../lifestyles/components/buttons/LifestyleSearchProductsButton';
import { ThreeDStockLifestylesListItemSimilarSearchButton } from '../ThreeDStockLifestylesListItemSimilarSearchButton';

import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { DropdownHelper } from '../../../../../../../helpers/dropdowns/DropdownHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { NextLinkHelper } from '../../../../../../../helpers/links/NextLinkHelper';
import { PopoverPlacement } from '../../../../../../../helpers/Popover/popoverConstants';
import { Translate } from '../../../../../../../helpers/Translate';

import { LifestylesPermissions } from '../../../../../../lifestyles/lifestylesConstants';

import { aiSearchKeys, words } from '../../../../../../../locales/keys';

interface ThreeDStockLifestylesListItemButtonsProps {
  lifestyle: ThreeDStockLifestylesListItemButtonsLifestyle;
  downloadButtonAction: string;
  downloadSeparatelyButtonAction: string;
  lifestyleEditPagePath: string;
  updateLifestyleCache?: UpdateLifestyleCacheAction<ThreeDStockLifestylesListItemButtonsLifestyle>;
  onEditButtonMouseEnter?: () => void;
  onLifestyleSimilarSearchFilter?: OnLifestyleSimilarSearchAction;
  companyNanoId?: TeamNanoID;
  isMyLibrary?: boolean;
}

function ThreeDStockLifestylesListItemButtons({
  lifestyle,
  downloadButtonAction,
  downloadSeparatelyButtonAction,
  updateLifestyleCache,
  onEditButtonMouseEnter,
  lifestyleEditPagePath,
  onLifestyleSimilarSearchFilter,
  companyNanoId,
  isMyLibrary
}: ThreeDStockLifestylesListItemButtonsProps) {
  return (
    <div className="flex justify-between gap-1">
      <div className="min-w-0">
        <CheckPermissions
          action={LifestylesPermissions.READ_LIFESTYLE_FAVORITE_BUTTON}
        >
          <LifestyleFavoriteButton
            dataGa="lifestyles-lifestyle-favorite-button"
            lifestyle={lifestyle}
            icon={
              lifestyle.favorite
                ? IconsEnum.HEART_SOLID
                : IconsEnum.HEART_OUTLINE
            }
            iconClassName="h-5 w-5 shrink-0"
            i18nText={
              lifestyle.favorite ? words.inFavorites : words.toFavorites
            }
            i18nTextClassName="truncate"
            updateIndexLifestyleCache={updateLifestyleCache}
            className={
              lifestyle.favorite
                ? 'py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-pink-500 hover:bg-pink-600 shadow-sm hover:shadow-md max-w-full transition-colors duration-150 ease-out'
                : 'py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-black hover:bg-gray-900 shadow-sm hover:shadow-md max-w-full transition-colors duration-150 ease-out'
            }
          />
        </CheckPermissions>
      </div>

      <div className="flex gap-1">
        <CheckPermissions action={downloadButtonAction}>
          <DownloadLifestyleButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="lifestyles-lifestyle-download-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            lifestyleUuid={lifestyle.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <CheckPermissions action={downloadSeparatelyButtonAction}>
          <DownloadLifestyleSeparatelyButton
            className="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
            dataGa="lifestyles-lifestyle-download-separately-button"
            icon={IconsEnum.DOWNLOAD_SOLID}
            iconClassName="h-5 w-5"
            lifestyleUuid={lifestyle.uuid}
            tooltipI18nText={words.download}
            tooltipSingleton
          />
        </CheckPermissions>

        <DropdownHelper
          className="relative flex items-center justify-center"
          buttonClassName="py-2.5 pl-2.5 pr-2.5 rounded-full inline-flex items-center leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
          icon={IconsEnum.DOTS_HORIZONTAL_OUTLINE}
          popoverClassName="flex flex-col items-end gap-1"
          dropdownPlacement={PopoverPlacement.TOP_END}
        >
          {({ closePopover }) => (
            <>
              <CheckPermissions
                action={
                  LifestylesPermissions.READ_LIFESTYLE_SEARCH_PRODUCTS_BUTTON
                }
              >
                <LifestyleSearchProductsButton
                  dataGa="lifestyles-lifestyle-search-products-button"
                  lifestyle={lifestyle}
                  className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
                  i18nText={aiSearchKeys.findProducts}
                  i18nTextClassName="inline"
                  icon={IconsEnum.SEARCH_AI_SOLID}
                  companyNanoId={companyNanoId}
                  isMyLibrary={isMyLibrary}
                />
              </CheckPermissions>

              <CheckPermissions
                action={LifestylesPermissions.READ_LIFESTYLE_ATTACH_BUTTON}
              >
                <LifestyleAttachModalButton
                  dataGa="lifestyles-lifestyle-attach-button"
                  className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
                  lifestyle={lifestyle}
                  i18nText={words.attach}
                  i18nTextClassName="inline"
                  icon={IconsEnum.PAPER_CLIP_SOLID}
                  iconClassName="w-5 h-5"
                />
              </CheckPermissions>

              <CheckPermissions
                action={
                  LifestylesPermissions.READ_LIFESTYLE_FIND_SIMILAR_FILTER_BUTTON
                }
              >
                <ThreeDStockLifestylesListItemSimilarSearchButton
                  dataGa="lifestyles-lifestyle-similar-search-filter-button"
                  onLifestyleSimilarSearch={onLifestyleSimilarSearchFilter}
                  onAfterClick={closePopover}
                  lifestyle={lifestyle}
                  className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
                  i18nText={aiSearchKeys.findSimilar}
                  i18nTextClassName="inline"
                  icon={IconsEnum.SEARCH_AI_SOLID}
                />
              </CheckPermissions>

              <CheckPermissions
                action={LifestylesPermissions.READ_LIFESTYLE_COPY_LINK_BUTTON}
              >
                <LifestyleCopyLinkButton
                  className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
                  dataGa="lifestyles-lifestyle-copy-link-button"
                  icon={IconsEnum.LINK_SOLID}
                  iconClassName="h-5 w-5"
                  lifestyleNanoId={lifestyle?.nanoId}
                  i18nText={words.copyLink}
                  i18nTextClassName="inline"
                  companyNanoId={companyNanoId}
                />
              </CheckPermissions>

              <CheckPermissions
                action={LifestylesPermissions.READ_LIFESTYLE_EDIT_BUTTON}
              >
                <NextLinkHelper
                  onMouseEnter={onEditButtonMouseEnter}
                  dataGa="lifestyles-lifestyle-edit-link"
                  href={lifestyleEditPagePath}
                  className="py-2.5 pl-2.5 pr-5 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-gray-900 bg-white hover:bg-gray-100 shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
                >
                  <Icon icon={IconsEnum.PENCIL} className="w-5 h-5" />
                  <span>
                    <Translate id={words.edit} />
                  </span>
                </NextLinkHelper>
              </CheckPermissions>
            </>
          )}
        </DropdownHelper>
      </div>
    </div>
  );
}

export default ThreeDStockLifestylesListItemButtons;
